import * as PIXI from "pixi.js-legacy"

import * as Utils from "./utils"

export default class Slice {

	get size() {
		// If the corresponding linkObject is clipped, return the possibly clipped size
		if (this._overflow === "clipped") {
			const { viewportSize } = this._player
			return {
				width: Math.min(this._naturalWidth * this._scale, viewportSize.width),
				height: Math.min(this._naturalHeight * this._scale, viewportSize.height),
			}
		}
		// Otherwise just return the actual size of the sprite in the viewport
		return {
			width: this._naturalWidth * this._scale,
			height: this._naturalHeight * this._scale,
		}
	}

	get container() { return this._container }

	// Only for a video slice
	get duration() { return this._duration }

	// For seeking a particular slice
	get path() { return (this._resourceData) ? this._resourceData.path : null }

	constructor(resourceData, overflow, fit, player) {
		this._resourceData = resourceData
		this._overflow = overflow
		this._fit = fit
		this._player = player

		this._naturalWidth = 0
		this._naturalHeight = 0
		this._duration = 0

		// Create the container that will hold the resource's texture(s)
		this._container = new PIXI.Container()
		// Create the sprite that will hold the image or fallback image
		this._sprite = this._createAndAddSprite()
	}

	_createAndAddSprite() {
		const sprite = new PIXI.Sprite()
		this._container.addChild(sprite)
		return sprite
	}

	finishBuilding() {
		// Create and apply the image or fallback image texture to the sprite
		this._sprite.texture = this._createTexturesForSprite()
if (!this._sprite.texture) {
	console.log(this.size)
}
		this._centerAndClipIfNeeded()
	}

	_centerAndClipIfNeeded() {
		// Center the sprite(s)
		this._sprite.anchor.set(0.5)
		if (this._playableSprite) {
			this._playableSprite.anchor.set(0.5)
		}

		// If the linkObject needs to be clipped, add a clipping mask
		if (this._overflow === "clipped") {
			this._mask = new PIXI.Graphics()
			this._container.addChild(this._mask)
			this._container.mask = this._mask
		}
	}

	_createTexturesForSprite() {
		let texture = null
		if (!this._resourceData) {
			return null
		}
		// Create the texture based on the resource's type
		const { path, mediaFragmentString, resourceType } = this._resourceData
		if (resourceType === "image") {
			texture = this._getTextureFromPath(path, mediaFragmentString)
		} else if (resourceType === "video") {
			const { fallbackPath, fallbackMediaFragmentString } = this._resourceData
			if (fallbackPath) {
				texture = this._getTextureFromPath(fallbackPath, fallbackMediaFragmentString)
			}

			// Create video element
			this._video = document.createElement("video")
			this._video.preload = "auto"
			this._video.loop = true // All videos will loop by default
			this._video.autoplay = false // Prevent autoplay at start
			this._video.muted = true // Only a muted video can autoplay
			this._video.src = path

			this._shouldPlay = false

			this._video.addEventListener("durationchange", this._doOnDurationChange.bind(this))
		}

		return texture
	}

	// _getTextureFromPath actually takes as arguments a path and mediaFragmentString if it exists
	_getTextureFromPath(path, mediaFragmentString = null) {
		if (!path) {
			return null
		}

		const pixiResources = PIXI.Loader.shared.resources
		if (!pixiResources || !pixiResources[path]) {
			return null
		}

		let { texture } = pixiResources[path]
		this._naturalWidth = texture.width
		this._naturalHeight = texture.height

		// If a media fragment has been defined, frame the texture
		// (note that, in that case, fit will apply to the fragment
		// rather than the entire uncropped resource)
		if (mediaFragmentString) {
			const result = Utils.parseMediaFragmentString(mediaFragmentString)
			if (!result) {
				return texture
			}
			const { unit } = result
			let {
				x, y, w, h,
			} = result
			const { width, height } = texture
			if (unit === "percent") {
				x *= width / 100
				y *= height / 100
				w *= width / 100
				h *= height / 100
			}

			// Correct potential mistakes in the way the media fragment was written
			// by limiting the fragment to the natural dimensions of the resource
			x = Math.min(x, width)
			y = Math.min(y, height)
			w = Math.min(w, width)
			h = Math.min(h, height)

			const { baseTexture } = texture
			const frame = new PIXI.Rectangle(x, y, w, h)
			texture = new PIXI.Texture(baseTexture, frame)
			this._naturalWidth = texture.width
			this._naturalHeight = texture.height
		}

		return texture
	}

	// Once a video's duration is different from zero, get useful information
	_doOnDurationChange() {
		const { duration, videoWidth, videoHeight } = this._video
		if (!duration) {
			return
		}

		const videoTexture = PIXI.Texture.from(this._video)
		// Also prevent autoplay at start via a PIXI specificity
		videoTexture.baseTexture.resource.autoPlay = false

		this._playableSprite = this._createAndAddSprite()
		this._playableSprite.texture = videoTexture
		this._playableSprite.anchor.set(0.5)

		this._naturalWidth = videoWidth
		this._naturalHeight = videoHeight
		this._duration = duration

		this.resize()

		// If the video had already been asked to play, play it now
		if (this._shouldPlay === true) {
			this._video.play()
		}
	}

	addSliceContainerToPageContainer(pageContainer) {
		pageContainer.addChild(this._container)
	}

	// Play a video if its metadata has been loaded - or record the fact it should play if not
	play() {
		if (this._video && this._video.duration) {
			if (this._naturalWidth && this._naturalHeight) {
				this._video.play()
			} else {
				this._shouldPlay = true
			}
		}
	}

	// Stop a video by pausing it and returning to its first frame
	stop() {
		if (this._video && this._video.duration) {
			this._video.pause()
			this._video.currentTime = 0
		}
	}

	resize() {
		this._applyFit()
		this._applyOverflow()
	}

	_applyFit() {
		if (!this._naturalWidth || !this._naturalHeight) {
			return
		}
		const naturalRatio = this._naturalWidth / this._naturalHeight
		const { viewportSize } = this._player
		const { width, height } = viewportSize

		// Compute the scale to be applied to the container based on fit
		const ratio = width / height
		let scale = 1
		switch (this._fit) {
		case "height":
			scale = this._getScaleWhenForcingHeight(height)
			break
		case "width":
			scale = this._getScaleWhenForcingWidth(width)
			break
		case "contain":
			if (naturalRatio >= ratio) {
				scale = this._getScaleWhenForcingWidth(width)
			} else {
				scale = this._getScaleWhenForcingHeight(height)
			}
			break
		case "cover":
			if (naturalRatio >= ratio) {
				scale = this._getScaleWhenForcingHeight(height)
			} else {
				scale = this._getScaleWhenForcingWidth(width)
			}
			break
		default:
			break
		}

		// Now apply the scale to the container
		this._container.scale.set(scale)
		this._scale = scale
	}

	_getScaleWhenForcingHeight(viewportHeight) {
		const scale = viewportHeight / this._naturalHeight
		return scale
	}

	_getScaleWhenForcingWidth(viewportWidth) {
		const scale = viewportWidth / this._naturalWidth
		return scale
	}

	// Size the clipping mask based on viewport size if the linkObject needs to be clipped
	_applyOverflow() {
		if (this._overflow === "clipped") {
			const scale = this._scale || 1
			const { viewportSize } = this._player
			const { width, height } = viewportSize
			this._mask.clear()
			this._mask.beginFill(0x0000FF)
			this._mask.drawRect((-width / scale) / 2, (-height / scale) / 2,
				width / scale, height / scale)
			this._mask.endFill()
		}
	}

	// Destroy a sprite by removing event listenes and destroying all textures
	destroy() {
		if (this._video) {
			this._video.removeEventListener("durationchange", this._doOnDurationChange.bind(this))
		}
		if (this._playableSprite) {
			this._destroySprite(this._playableSprite)
		}
		if (this._sprite) {
			this._destroySprite(this._sprite)
		}
	}

	_destroySprite(sprite) {
		const { texture } = sprite
		if (texture) {
			this._destroyTexture(texture)
		}
		sprite.destroy(({ children: true, texture: false, baseTexture: false }))
	}

	// Destroy the PIXI baseTexture first, then the actual texture
	_destroyTexture(texture) {
		if (texture) {
			const { baseTexture } = texture
			if (baseTexture) {
				baseTexture.destroy()
			}
			const shouldDestroyBaseTexture = false
			texture.destroy(shouldDestroyBaseTexture)
		}
	}

}